import { Breakpoints, intl, useBreakpoint } from '../../lib';
import type { Ref } from 'react';
import React, { forwardRef, useCallback, useMemo, useRef } from 'react';
import { HBox } from '../layout';
import { Tool } from './Tool';
import type { LanguageProperty } from '../generated/LanguageProperty';
import { DEFAULT_PLACEMENT_CONFIG, mergeRefs } from '../../util';
import { Arrow, useToggleLayer } from 'react-laag';
import FocusTrap from 'focus-trap-react';

/**
 * This might cause some memory to be allocated longer than it ideally should be.
 *
 * As soon as we update beyond typescript 4.3.* we can replace it with this.
 * https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/WeakRef
 */
let closeModalPrev: null | (() => void) = null;

export type InfoModalProps = {
  settings: {
    modalTitle?: LanguageProperty;
    modalContent?: LanguageProperty | JSX.Element;
    modalList?: LanguageProperty[];
    orderedList: boolean;
    heading: LanguageProperty;
    removeArrow?: boolean;
    ariaLabel: LanguageProperty;
    tooltipText?: LanguageProperty;
  };
};

export function InfoModal({
  settings,
  ...props
}: Readonly<InfoModalProps>): JSX.Element {
  const mobile = useBreakpoint(Breakpoints.MOBILE);
  const dialog = useRef<HTMLDialogElement>(null);
  const getList = useMemo((): JSX.Element => {
    if (settings.modalList && settings.modalList.length > 0) {
      const getItems = () => {
        return (settings.modalList ?? []).map(item => (
          <li key={item}>{intl(item)}</li>
        ));
      };
      if (settings.orderedList) {
        return <ol>{getItems()}</ol>;
      } else {
        return <ul>{getItems()}</ul>;
      }
    } else {
      return <></>;
    }
  }, [settings.modalList, settings.orderedList]);

  const modalTitle = useMemo(() => {
    return settings.modalTitle ? (
      <h4 className="ehs-modalTitleH4">{intl(settings.modalTitle)}</h4>
    ) : (
      <div className="ehs-modalTitleEmpty" />
    );
  }, [settings.modalTitle]);

  const [element, toggleLayerProps] = UseCreateToggleLayer({
    modalTitle: modalTitle,
    modalContent: settings.modalContent,
    list: getList,
    removeArrow: settings.removeArrow
  });

  const close = useCallback(() => {
    toggleLayerProps.close();
  }, [toggleLayerProps]);

  const open = useCallback(
    (event: React.MouseEvent<never>) => {
      if (mobile) {
        dialog.current?.showModal();
        return;
      }
      if (closeModalPrev != null) {
        closeModalPrev();
      }
      closeModalPrev = toggleLayerProps.close;
      toggleLayerProps.openFromMouseEvent(event);
    },
    [mobile, toggleLayerProps]
  );

  return (
    <HBox {...props}>
      {!mobile ? (
        element
      ) : (
        <InfoModalDialogComponent
          modalTitle={modalTitle}
          modalContent={settings.modalContent}
          list={getList}
          removeArrow={settings.removeArrow}
          ref={dialog}
        />
      )}
      {settings.heading && (
        <h3 className="ehs-infoModalHeading">{intl(settings.heading)}</h3>
      )}
      <div className="ehs-infoModalIconWrapper">
        <Tool
          onClick={toggleLayerProps.isOpen ? close : open}
          icon="shui_information"
          text=""
          aria-label={intl(settings.ariaLabel)}
          tooltip={
            settings.tooltipText ? intl(settings.tooltipText) : undefined
          }
        />
      </div>
    </HBox>
  );
}

type InfoModalComponentProps = {
  modalTitle: JSX.Element;
  modalContent?: LanguageProperty | JSX.Element;
  list: JSX.Element;
  removeArrow?: boolean;
};
const InfoModalDialogComponent = forwardRef(function InfoModalDialogComponent(
  { modalTitle, modalContent, list }: InfoModalComponentProps,
  ref: Ref<HTMLDialogElement> | undefined
): JSX.Element {
  const dialog = useRef<HTMLDialogElement>(null);
  return (
    <dialog ref={mergeRefs([ref, dialog])} className="ehs-infoModalDialog">
      <HBox className="ehs-infoModalTitle">
        {modalTitle}
        <Tool
          icon={'materialicon-close'}
          onClick={() => dialog.current?.close()}
          text=""
          aria-label={intl('view.button.close')}
        />
      </HBox>
      <div className="ehs-modalContent">
        {modalContent && typeof modalContent == 'string' ? (
          <span>{intl(modalContent)}</span>
        ) : (
          modalContent
        )}
        {list}
      </div>
    </dialog>
  );
});

function UseCreateToggleLayer({
  modalTitle,
  modalContent,
  list,
  removeArrow
}: Readonly<InfoModalComponentProps>) {
  return useToggleLayer(
    ({ isOpen, layerProps, close, layerSide, arrowStyle }) => {
      return (
        isOpen && (
          <FocusTrap
            active={isOpen}
            focusTrapOptions={{
              initialFocus: '.ehs-infoModalWrapper ',
              escapeDeactivates: false,
              allowOutsideClick: true
            }}
          >
            <div
              className="ehs-infoModalWrapper"
              {...layerProps}
              style={{ ...layerProps.style }}
            >
              <div className="ehs-infoModal" aria-live="polite">
                <HBox className="ehs-infoModalTitle">
                  <div className="header-container">
                    <span>{modalTitle}</span>
                    <Tool
                      icon={'materialicon-close'}
                      onClick={close}
                      text=""
                      aria-label={intl('view.button.close')}
                    />
                  </div>
                </HBox>
                <div className="ehs-modalContent">
                  {modalContent && typeof modalContent == 'string' ? (
                    <span>{intl(modalContent)}</span>
                  ) : (
                    modalContent
                  )}
                  {list}
                </div>
              </div>
              {!removeArrow && (
                <Arrow
                  size={10}
                  roundness={0}
                  layerSide={layerSide}
                  borderWidth={1}
                  backgroundColor="var(--base-0)"
                  borderColor="var(--base-1)"
                  style={{
                    ...arrowStyle
                  }}
                />
              )}
            </div>
          </FocusTrap>
        )
      );
    },
    {
      placement: DEFAULT_PLACEMENT_CONFIG,
      closeOnOutsideClick: true
    }
  );
}
